"use client";

import { NFONE_SUPPORT_EMAIL } from "@newform/constants";
import { Settings } from "lucide-react";
import Link from "next/link";
import { usePathname, useRouter, useSearchParams } from "next/navigation";
import { useTranslations } from "next-intl";
import { type CSSProperties, type ReactNode, useMemo, useState } from "react";
import { Button } from "@/components/ui/design-system/button";
import { trpc } from "@/lib/trpc-client";
import { cn } from "@/lib/utils";
import { useCreatorAuth } from "./creatorAuthContext";
import {
  DEPTH_ROOT_ID,
  HEADER_H,
  RAIL_MOTION,
  RAIL_W,
  RAIL_W_COLLAPSED,
  TABBAR_H,
} from "./one/chrome";
import { SideRail } from "./one/SideRail";
import { TabBar } from "./one/TabBar";
import { TopBar } from "./one/TopBar";
import { TAB_PATH, type Tab, tabForPath } from "./one/tabs";
import { useRailCollapsed } from "./one/useRailCollapsed";
import { PortalFooter } from "./PortalFooter";
import { ProgramSwitcher } from "./ProgramSwitcher";
import { PortalSettingsProvider } from "./portalSettings";
import { SettingsDialog } from "./SettingsDialog";
import { useCreatorHandles } from "./useCreatorHandles";
import { portalHref, useCreatorPortalAccess } from "./useCreatorPortalAccess";

/**
 * Where each tab lives. The rail and the bar own the labels, icons and order;
 * this is only the routing the prototype had no need for.
 */
/** 1024 is the prototype's WIDE, which is Tailwind's `lg`. */

export function PortalShell({ children }: { children: ReactNode }) {
  const access = useCreatorPortalAccess();
  const auth = useCreatorAuth();
  const pathname = usePathname();
  /*
    A THREAD ON A PHONE IS A CHAT APP, NOT A PAGE.

    Open a conversation below rail width and the screen is that
    conversation: the log fills the width, the composer sits on the bottom
    edge, and nothing else is on screen. The shell owns the padding and the
    footer, so the shell is what has to step back — a thread cancelling the
    column's gutters from inside would have to know them, and they change at
    `md` while the thread's own breakpoint is `lg`.

    Safe to read the query here: the layout wraps this in `Suspense`, which
    is what `useSearchParams` needs during a static render.
  */
  // Hoisted, not folded into the `&&` below: short-circuiting it would make
  // the hook conditional on the route.
  const conversation = useSearchParams().get("conversation");
  const inboxPage = pathname === "/one/inbox";
  const chatOpen = inboxPage && !!conversation;
  // A video's review: the cut beside its thread, one object that fills the
  // space it is given, so past rail width the page is the space (the same
  // argument as the inbox below) and the footer stays out of the column.
  const reviewPage = /^\/one\/videos\/[^/]+$/.test(pathname);
  const screenPage = inboxPage || reviewPage;
  const router = useRouter();
  const t = useTranslations("shell");
  const messaging = trpc.creatorMessaging.auth.config.useQuery();
  const supportEmail = messaging.data?.supportEmail ?? NFONE_SUPPORT_EMAIL;
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [railCollapsed, setRailCollapsed] = useRailCollapsed();
  // Stable, so a page holding it in a dependency list does not re-run on every
  // render of the shell.
  const settings = useMemo(() => ({ open: () => setSettingsOpen(true) }), []);

  // The handles the settings sheet edits. Read here rather than passed down
  // from Home, because the sheet opens from the chrome and so has to work on
  // every tab. It is the same query key Home uses, so on Home it is already in
  // cache and this costs nothing.
  const home = trpc.creatorPortal.getPortalHome.useQuery(access ?? {}, {
    enabled: !!access,
    retry: false,
  });
  // Unconditional, as a hook has to be — the sheet it feeds is only mounted
  // once there is access, but the form state behind it can exist before then.
  const handles = useCreatorHandles({
    access: access ?? {},
    tiktokHandle: home.data?.creator.tiktokHandle ?? null,
    instagramHandle: home.data?.creator.instagramHandle ?? null,
  });

  const tab: Tab = tabForPath(pathname);

  const go = (next: Tab) => router.push(portalHref(TAB_PATH[next], access));

  return (
    <div
      // The rail's width lives in one place and both the rail and the content
      // offset read it. Two copies of 13rem is a drift waiting to happen, and
      // the symptom would be a sliver of content under the nav.
      style={
        {
          "--one-rail": railCollapsed ? RAIL_W_COLLAPSED : RAIL_W,
          "--one-tabbar": `${TABBAR_H}px`,
          "--one-header": `${HEADER_H}px`,
          // What the device owes the bottom edge, which the prototype's
          // simulated frame published and a real browser has to be asked for.
          // 12px rather than 0 as the fallback — the same value the prototype
          // uses for a browser on a desk, where there is no home indicator but
          // a tab target flush against the window edge still reads as cut off.
          "--one-inset": "env(safe-area-inset-bottom, 12px)",
        } as CSSProperties
      }
      id={DEPTH_ROOT_ID}
      className="min-h-screen bg-nf-ds-bg"
    >
      {/*
        The rail and the bar are the prototype's own components, moved here
        rather than rebuilt: they carry the unread dots, the submit tile and
        the press behaviour that a rewrite from their docstrings dropped.

        Both render and one is hidden with `display: none`, which takes it out
        of the accessibility tree too — so a reader never meets the same five
        destinations twice.
      */}
      {/*
        `flex`, not `block`, so the rail fills the column.

        The rail sizes to its content as a block child, which left it a short
        panel floating at the top of the window — and, less visibly, meant the
        `mt-auto` holding its account row down had no free space to push into,
        so the foot of the rail was never at the foot. The prototype's host is
        `absolute inset-0 flex` and stretches it by default; this is the same
        thing said to a fixed element.
      */}
      <div className="fixed inset-y-0 left-0 z-20 hidden lg:flex">
        <SideRail
          tab={tab}
          onTab={go}
          unread={0}
          unseen={0}
          switcher={<ProgramSwitcher access={access} compact={railCollapsed} />}
          email={auth.account?.email ?? ""}
          onSettings={() => setSettingsOpen(true)}
          collapsed={railCollapsed}
          onCollapsedChange={setRailCollapsed}
        />
      </div>

      {/*
        Below the rail's width there is no rail, so this is the only place the
        program can be switched — the shipped header carried `ProgramSwitcher`
        at every width, and moving it into the rail alone took program
        switching away from every creator on a phone.

        Positioned by the host for the same reason the tab bar is: the bar is
        `absolute` against the prototype's frame, and a zero-height fixed strip
        is what stands in for that frame here.
      */}
      <div className="fixed inset-x-0 top-0 z-20 lg:hidden">
        <TopBar
          tab={tab}
          wide={false}
          topInset="env(safe-area-inset-top, 0px)"
          columnClassName="max-w-6xl px-nf-ds-6"
          homeHref={portalHref("/one", access)}
          trailing={
            // There is no rail below this width, so the header carries the
            // only way in — beside the program, because the two of them are
            // the same kind of thing: what you are looking at, and who you are
            // looking at it as.
            <div className="flex min-w-0 items-center gap-nf-ds-1">
              <ProgramSwitcher access={access} presentation="sheet" />
              <button
                type="button"
                onClick={() => setSettingsOpen(true)}
                aria-label={t("nav.settings")}
                className={cn(
                  "-mr-nf-ds-2 grid size-[36px] shrink-0 place-items-center rounded-[10px]",
                  "text-muted-foreground outline-hidden hover:text-nf-ds-fg",
                  "focus-visible:ring-2 focus-visible:ring-nf-ds-brand",
                )}
              >
                <Settings className="size-[18px]" strokeWidth={2} />
              </button>
            </div>
          }
        />
      </div>

      {/*
        Clearance for both bars belongs to everything that scrolls under them,
        not to `main` alone — the footer sits outside `main` and was landing
        behind the tab bar, which put "Sign out" under a tab target.

        Exactly each bar's height plus the inset it clears, matching the
        prototype's scroll area. An extra gap on top of that reads as a dead
        strip and, since it is the last thing under the bar that gives the
        glass something to blur, leaves the material looking flat at the end of
        every page.
      */}
      <div
        className={cn(
          // A column the height of the window, so `main` has something to
          // fill. Without it `main` is only as tall as its content, and a page
          // that wants to centre itself vertically has no space to centre in —
          // it can only centre inside its own height, which is a no-op. The
          // footer being pinned to the bottom edge on a short page rather than
          // floating under the last card is the same fix, seen from the other
          // end.
          "flex min-h-svh flex-col",
          // An open thread is exactly the viewport, not at least it. With
          // `min-h-svh` alone a long conversation grows the column and the
          // page scrolls, which takes the composer off the bottom edge and
          // the header off the top — the two things a chat keeps still.
          // Pinned to `h-svh` the column cannot grow, so the only thing
          // left that can scroll is the message log inside it.
          // `dvh`, not `svh`. `svh` is the viewport with the browser's own
          // chrome at its largest and says nothing about the keyboard; with
          // `interactive-widget=resizes-content` the layout viewport shrinks
          // when the keyboard opens, and a column still asking for `100svh`
          // is then taller than the space it has — with `overflow-hidden`
          // above it, the composer is simply cut off below the fold.
          // `dvh` tracks the viewport it actually has.
          chatOpen && "max-lg:h-dvh max-lg:min-h-0 max-lg:overflow-hidden",
          // And at rail width, for the whole inbox rather than just an open
          // thread: the two-pane layout is one object that fills the space
          // it is given, so the page has to be the space. Without this the
          // pane grows to the taller column and the window scrolls, which
          // moves the list and the composer as one and leaves the thread
          // header somewhere above the fold.
          screenPage && "lg:h-svh lg:min-h-0 lg:overflow-hidden",
          "pt-[calc(var(--one-header)+env(safe-area-inset-top,0px))] lg:pt-0",
          "pb-[calc(var(--one-tabbar)+var(--one-inset))] lg:pb-0",
          "lg:pl-[var(--one-rail)]",
          // The rail's own curve, so the page slides with its edge rather
          // than jumping to where the edge will be.
          "transition-[padding]",
          RAIL_MOTION,
        )}
      >
        <main
          className={cn(
            // 16 on a phone, 32 past rail width. The scroll area already
            // clears the header by exactly the bar's height, and the screen
            // inside adds its own 16 — so a further 32 on top put 48 points
            // between the wordmark and the page's first line, on the width
            // with the least room to spend.
            // `flex-1` takes the space the column has spare; `flex-col` is
            // what lets a page opt into using it (`flex-1` on its own root).
            // A page that does not opt in stacks exactly as it did before —
            // children on a column's main axis keep their natural height.
            "flex w-full flex-1 flex-col",
            "mx-auto max-w-6xl px-nf-ds-6 py-nf-ds-4 lg:py-nf-ds-8",
            pathname === "/one/inbox" &&
              "px-nf-ds-3 py-nf-ds-3 md:px-nf-ds-6 md:py-nf-ds-8",
            // Edge to edge for an open thread, below rail width only, and
            // `min-h-0` so `flex-1` can shrink it rather than only grow it.
            chatOpen && "max-lg:min-h-0 max-lg:px-0 max-lg:py-0",
            screenPage && "lg:min-h-0",
          )}
        >
          <PortalSettingsProvider value={settings}>
            {children}
          </PortalSettingsProvider>
        </main>

        {/*
          Updates ends with its own control on a phone.

          Its email switch sits at the foot of the page, where a thumb can
          reach it, and "Need a hand? Message us · Support" landing directly
          under it put two unrelated rows in the same corner: a setting and
          a pair of links, competing for the same reach. The page already
          answers the question the footer asks, because Inbox is one tap away
          in the tab bar.

          Only on a phone, and only here. At rail width the footer is at the
          bottom of a long column with nothing near it, and every other
          screen ends in content rather than a control.
        */}
        {/*
          Not on the inbox, at any width.

          The footer exists to say "here is how to reach us", and its one
          real link points at the inbox — it already suppresses that link on
          this page, which is the tell that it has nothing left to offer
          here. At rail width it was also spending 121 points of a locked
          viewport to say it, directly under the pane holding the
          conversation, which is the height the thread wanted.

          Updates hides it on a phone for a different reason: its email
          switch sits on that edge and two unrelated rows were competing for
          the same thumb.
        */}
        <div
          className={cn(
            inboxPage && "hidden",
            reviewPage && "hidden",
            pathname === "/one/updates" && "max-lg:hidden",
          )}
        >
          <PortalFooter
            supportEmail={supportEmail}
            signedIn={!!auth.account}
            inboxHref={
              pathname === "/one/inbox"
                ? null
                : portalHref("/one/inbox", access)
            }
            signInHref="/join/link"
            hideHelpOnDesktop={pathname === "/one"}
          />
        </div>
      </div>

      {/*
        The bar is `absolute` inside itself, because in the prototype the
        simulated phone frame was its containing block. There is no frame here,
        so an absolute bar resolves against the initial containing block and
        lands at the bottom of the *first viewport* — floating over the page
        mid-scroll instead of sitting on the bottom edge. The host supplies the
        containing block, exactly as it does for the rail above, rather than the
        component being forked to know which surface it is on.

        A zero-height fixed strip on the bottom edge is all that takes: the bar
        draws upward from it, and nothing about the component changes.
      */}
      <div className="fixed inset-x-0 bottom-0 z-20 lg:hidden">
        <TabBar tab={tab} onTab={go} unread={0} unseen={0} />
      </div>

      {access ? (
        <SettingsDialog
          open={settingsOpen}
          onOpenChange={setSettingsOpen}
          email={auth.account?.email ?? ""}
          depthRootId={DEPTH_ROOT_ID}
          handles={handles}
          onSignOut={() => void auth.signOut?.()}
        />
      ) : null}
    </div>
  );
}

function PortalNotice({
  title,
  body,
  action,
}: {
  title: string;
  body: string;
  action?: ReactNode;
}) {
  return (
    <div className="bg-card border border-border rounded-nf-ds-lg p-nf-ds-8 shadow-nf-ds-xs max-w-xl mx-auto mt-nf-ds-12 text-center">
      <h1 className="font-nf-ds-sans text-[18px] font-[600] leading-[1.25] tracking-[-0.005em] mb-nf-ds-2">
        {title}
      </h1>
      <p className="font-nf-ds-sans text-[14px] text-muted-foreground">
        {body}
      </p>
      {action ? <div className="mt-nf-ds-6">{action}</div> : null}
    </div>
  );
}

export function InvalidLinkNotice() {
  const t = useTranslations("shell.notices.invalidLink");
  return (
    <PortalNotice
      title={t("title")}
      body={t("body")}
      action={
        <Button variant="brand" onClick={() => window.location.reload()}>
          {t("action")}
        </Button>
      }
    />
  );
}

export function AccessEndedNotice() {
  const t = useTranslations("shell.notices.accessEnded");
  return (
    <PortalNotice
      action={
        <Link href="/one/inbox" className="underline">
          {t("action")}
        </Link>
      }
      title={t("title")}
      body={t("body", { email: NFONE_SUPPORT_EMAIL })}
    />
  );
}

/**
 * Both codes are terminal for the portal, so pages render a notice rather than
 * a retry: UNAUTHORIZED means the access itself is bad, FORBIDDEN means the
 * roster no longer includes this creator (or the program is paused). Returns
 * null for anything else so ordinary failures keep their retry path.
 */
export function portalAccessNotice(code: string | undefined) {
  if (code === "UNAUTHORIZED") return <InvalidLinkNotice />;
  if (code === "FORBIDDEN") return <AccessEndedNotice />;
  return null;
}
